import { FileDto } from './file';
import { AccountDto } from './account';
import { HashTagDto } from './hashtag';
import { PlaceDto } from './place';
import { FeedProductLinkDto } from './product';
import { ky } from './ky';
import { GeoLocation, ListResponse } from './common';

export type FeedSortType = 'LATEST' | 'ACCURACY' | 'NEAREST';

export interface PointDto {
  x: number;
  y: number;
}

export interface FeedFileDto {
  id?: number;
  file: FileDto;
  links: FeedFileLinkDto[];
  sortOrder: number;
}

export interface FeedFileLinkDto {
  id?: number;
  place?: PlaceDto;
  account?: AccountDto;
  point?: PointDto;
  sortOrder: number;
}

export interface FeedLinkDto {
  id?: number;
  place?: PlaceDto;
  account?: AccountDto;
  product?: FeedProductLinkDto;
  sortOrder: number;
}

export type FeedButtonStyle = 'PRIMARY' | 'SECONDARY' | 'OUTLINE' | 'DANGER';
export type FeedButtonTarget = 'WEB' | 'WEB_PC' | 'APP' | 'IOS_APP' | 'ANDROID_APP' | 'IOS_WEB' | 'ANDROID_WEB' | 'ALL';

export interface FeedButtonDto {
  id: number;
  label: string;
  url: string;
  style: FeedButtonStyle;
  target: FeedButtonTarget;
  sortOrder: number;
}

export interface FeedDto {
  sid?: string;
  owner?: AccountDto;
  caption: string;
  files: FeedFileDto[];
  links: FeedLinkDto[];
  hashtags: HashTagDto[];
  buttons?: FeedButtonDto[];
  likeCount?: number;
  commentCount?: number;
  shareCount?: number;
  viewCount?: number;
  isLike?: boolean;
  createdAt?: string;
}

export interface SearchFeedLinkParams {
  placeIds?: number[];
  accountIds?: number[];
}

export interface SearchFeedParams {
  keyword?: string;
  accountName?: string;
  hashTag?: string;
  location?: GeoLocation;
  distance?: string; // example: "100m",
  page?: number;
  size?: number;
  link?: SearchFeedLinkParams;
  beforeCreatedAt?: string;
  sortType?: FeedSortType;
}

export default class FeedService {

  /**
   * 피드 단건 가져오기
   *
   * @param id 피드 ID
   */
  async get(sid: string) {
    const response = await ky.get(`v1/feeds/${sid}`)
    return await response.json() as FeedDto;
  }

  /**
   * 피드 작성
   *
   * @param feed
   */
  async write(feed: FeedDto) {
    const response = await ky.post('v1/feeds', {
      json: feed,
    });
    return await response.json() as FeedDto;
  }

  /**
   * 피드 수정
   *
   * @param feed
   */
  async update(feed: FeedDto) {
    return await ky.put(`v1/feeds/${feed.sid}`, {
      json: feed,
    });
  }

  /**
   * 피드 삭제
   *
   * @param sid 피드 ID
   */
  async delete(sid: string) {
    return await ky.delete(`v1/feeds/${sid}`);
  }

  /**
   * 피드 검색
   *
   * @param params 검색 파라미터
   */
  async search(params: SearchFeedParams) {
    const searchParams = new URLSearchParams();

    if (params.keyword) searchParams.append('keyword', params.keyword);
    if (params.accountName) searchParams.append('accountName', params.accountName);
    if (params.hashTag) searchParams.append('hashTag', params.hashTag);
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.size) searchParams.append('size', params.size.toString());
    if (params.beforeCreatedAt) searchParams.append('beforeCreatedAt', params.beforeCreatedAt);
    if (params.sortType) searchParams.append('sortType', params.sortType);

    // Add location parameters if provided
    if (params.location) {
      searchParams.append('location.lat', params.location.lat.toString());
      searchParams.append('location.lon', params.location.lon.toString());
    }
    if (params.distance) searchParams.append('distance', params.distance);

    // Add link parameters if provided
  if (params.link) {
      if (params.link.placeIds && params.link.placeIds.length > 0) {
        params.link.placeIds.forEach(placeId => {
          searchParams.append('link.placeIds', placeId.toString());
        });
      }
      if (params.link.accountIds && params.link.accountIds.length > 0) {
        params.link.accountIds.forEach(accountId => {
          searchParams.append('link.accountIds', accountId.toString());
        });
      }
    }

    const response = await ky.get(`v1/feeds?${searchParams.toString()}`);
    return await response.json() as ListResponse<FeedDto>;
  }

  /**
   * 피드 조회수 증가
   *
   * @param id 피드 ID
   */
  async view(id: string) {
    return await ky.post(`v1/feeds/${id}/views`);
  }

  async report(id: string, reason: string) {
    return await ky.post(`v1/feeds/${id}/reports`, {
      json: { reason },
    });
  }

  async related(sid: string, params?: { page?: number; size?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.size) searchParams.append('size', params.size.toString());
    const query = searchParams.toString();
    const response = await ky.get(`v1/feeds/${sid}/related${query ? `?${query}` : ''}`);
    return await response.json() as ListResponse<FeedDto>;
  }
}
